import { formatMeters, scaleBarMeters } from './geofenceForm.js';

/**
 * An HONEST radius preview (design doc §13: V1 is a coordinate/radius SVG with
 * a distance scale — never a broken tile iframe, never an external request).
 *
 * What it shows is exactly what it knows: the circle a punch is tested against
 * (drawn at a fixed pixel radius), the radius written out, and a scale bar
 * whose length is real arithmetic — `scaleBarMeters(radius)` meters at the
 * same meters-per-pixel as the circle. What it does not know — streets,
 * buildings, anything a map tile would imply — it does not draw.
 */
const CIRCLE_PX = 56;
const W = 240;
const H = 168;
const CX = W / 2;
const CY = 72;

export function RadiusPreview({ radiusMeters }: { radiusMeters: number | null }) {
  if (radiusMeters == null) {
    return (
      <p className="rn-muted" style={{ fontSize: 13 }} data-testid="radius-preview-empty">
        The preview appears once the radius is a whole number of meters within bounds.
      </p>
    );
  }

  const barMeters = scaleBarMeters(radiusMeters);
  const barPx = (barMeters / radiusMeters) * CIRCLE_PX;

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`Geofence preview: punches within ${formatMeters(radiusMeters)} of the center count as at this work site. Scale: ${formatMeters(barMeters)}.`}
      style={{ maxWidth: '100%' }}
    >
      {/* The fence: the circle a punch's distance-to-center is tested against. */}
      <circle
        cx={CX}
        cy={CY}
        r={CIRCLE_PX}
        fill="var(--color-sky-soft, #dbeafe)"
        fillOpacity={0.5}
        stroke="var(--color-navy, #1e3a5f)"
        strokeWidth={1.5}
      />
      {/* The center — the stored latitude/longitude. */}
      <circle cx={CX} cy={CY} r={3} fill="var(--color-navy, #1e3a5f)" />
      {/* The radius, drawn and written. */}
      <line
        x1={CX}
        y1={CY}
        x2={CX + CIRCLE_PX}
        y2={CY}
        stroke="var(--color-navy, #1e3a5f)"
        strokeWidth={1}
        strokeDasharray="3 3"
      />
      <text
        x={CX + CIRCLE_PX / 2}
        y={CY - 6}
        textAnchor="middle"
        fontSize={12}
        fill="var(--color-navy, #1e3a5f)"
      >
        {formatMeters(radiusMeters)}
      </text>
      {/* The distance scale: barMeters at the circle's own meters-per-pixel. */}
      <line
        x1={CX - barPx / 2}
        y1={H - 22}
        x2={CX + barPx / 2}
        y2={H - 22}
        stroke="currentColor"
        strokeWidth={2}
      />
      <line
        x1={CX - barPx / 2}
        y1={H - 27}
        x2={CX - barPx / 2}
        y2={H - 17}
        stroke="currentColor"
        strokeWidth={2}
      />
      <line
        x1={CX + barPx / 2}
        y1={H - 27}
        x2={CX + barPx / 2}
        y2={H - 17}
        stroke="currentColor"
        strokeWidth={2}
      />
      <text x={CX} y={H - 6} textAnchor="middle" fontSize={12} fill="currentColor">
        {formatMeters(barMeters)}
      </text>
    </svg>
  );
}
